import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, ImageField } from "@/components/admin/fields";

export const BannerTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data;
      s.instagram_reels = (s.instagram_reels || []).map((x) => (typeof x === "string" ? { url: x, product_id: "" } : x));
      setSettings(s);
    }).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  if (!settings) return <p className="text-zinc-400 text-sm py-12">Loading banner settings…</p>;

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setBanner = (i, k, v) =>
    setSettings((s) => ({ ...s, banners: s.banners.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const setReel = (i, k, v) =>
    setSettings((s) => ({ ...s, instagram_reels: s.instagram_reels.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        announcement: settings.announcement,
        marquee_items: settings.marquee_items,
        banners: settings.banners,
        instagram_reels: settings.instagram_reels || [],
        instagram_profile: settings.instagram_profile || "",
      });
      toast.success("Homepage settings updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-10" data-testid="banner-tab">
      <div className="grid grid-cols-1 gap-6">
        <Field label="Announcement Bar" value={settings.announcement} onChange={(e) => set("announcement", e.target.value)} data-testid="banner-announcement" />
        <div>
          <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Marquee Items (one per line)</label>
          <textarea
            rows={3}
            value={(settings.marquee_items || []).join("\n")}
            onChange={(e) => set("marquee_items", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
            data-testid="banner-marquee"
            className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
          />
        </div>
      </div>

      <div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-6">Homepage Banners ({settings.banners.length})</p>
        <div className="space-y-8">
          {settings.banners.map((b, i) => (
            <div key={i} className="border border-zinc-200 p-6 space-y-5" data-testid={`banner-item-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-serif text-xl text-zinc-900">Banner {i + 1} — {b.title || "Untitled"}</span>
                <button
                  onClick={() => setSettings((s) => ({ ...s, banners: s.banners.filter((_, j) => j !== i) }))}
                  data-testid={`banner-delete-${i}`}
                  className="p-2 text-zinc-400 hover:text-red-500 transition-colors duration-200"
                  aria-label="Remove banner"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <Field label="Title" value={b.title} onChange={(e) => setBanner(i, "title", e.target.value)} data-testid={`banner-title-${i}`} />
                <Field label="Button Link (e.g. /shop?brand=x)" value={b.cta} onChange={(e) => setBanner(i, "cta", e.target.value)} data-testid={`banner-cta-${i}`} />
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Image Fit</label>
                  <select
                    value={b.fit || "cover"}
                    onChange={(e) => setBanner(i, "fit", e.target.value)}
                    data-testid={`banner-fit-${i}`}
                    className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60"
                  >
                    <option value="cover">Fill frame (wide photos)</option>
                    <option value="top">Fill frame, keep top (tall photos, faces)</option>
                    <option value="contain">Fit whole image (square/poster art)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 block mb-2">Subtitle</label>
                <textarea
                  rows={2}
                  value={b.subtitle}
                  onChange={(e) => setBanner(i, "subtitle", e.target.value)}
                  data-testid={`banner-subtitle-${i}`}
                  className="w-full bg-zinc-100 border border-zinc-200 text-zinc-900 text-sm px-4 py-3 focus:outline-none focus:border-gold/60 transition-colors duration-300"
                />
              </div>
              <ImageField label="Banner Image" value={b.image} onChange={(url) => setBanner(i, "image", url)} testid={`banner-image-${i}`} />
              <ImageField label="Mobile Banner Image (optional — portrait art for phones)" value={b.mobile_image || ""} onChange={(url) => setBanner(i, "mobile_image", url)} testid={`banner-mobile-image-${i}`} />
            </div>
          ))}
        </div>
        <button
          onClick={() => setSettings((s) => ({ ...s, banners: [...s.banners, { brand_slug: "", title: "", subtitle: "", image: "", cta: "/shop" }] }))}
          data-testid="banner-add-btn"
          className="mt-6 flex items-center gap-2 border border-gold/40 text-gold text-[10px] uppercase tracking-[0.3em] px-6 py-3 hover:bg-gold hover:text-white transition-colors duration-300"
        >
          <Plus size={14} /> Add Banner
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="reels-settings">
        <p className="text-sm font-medium text-[#111827] mb-1">Instagram Reels</p>
        <p className="text-xs text-gray-400 mb-4">Paste reel links — they show in the homepage Instagram slider and the Instagram icon at the top-right of the store.</p>
        <Field label="Instagram profile URL" value={settings.instagram_profile || ""} onChange={(e) => set("instagram_profile", e.target.value)} placeholder="https://www.instagram.com/aromacares" data-testid="instagram-profile-input" />
        <div className="space-y-3 mt-4">
          {(settings.instagram_reels || []).map((reel, i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-end gap-3 border border-gray-100 rounded-lg p-3" data-testid={`reel-url-row-${i}`}>
              <Field label={`Reel ${i + 1} URL`} value={reel.url} onChange={(e) => setReel(i, "url", e.target.value)} placeholder="https://www.instagram.com/reel/…" data-testid={`reel-url-${i}`} />
              <div className="flex-1">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Tag a product (shoppable chip)</label>
                <select value={reel.product_id || ""} onChange={(e) => setReel(i, "product_id", e.target.value)} data-testid={`reel-product-${i}`} className="w-full bg-white border border-gray-300 rounded-lg text-sm px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E80071]/30 focus:border-[#E80071]">
                  <option value="">No product tag</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <button
                type="button"
                onClick={() => set("instagram_reels", settings.instagram_reels.filter((_, j) => j !== i))}
                data-testid={`reel-remove-${i}`}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors shrink-0"
                aria-label="Remove reel"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
        <button
          onClick={() => set("instagram_reels", [...(settings.instagram_reels || []), { url: "", product_id: "" }])}
          data-testid="reel-add-btn"
          className="mt-4 flex items-center gap-2 border border-gold/40 text-gold text-[10px] uppercase tracking-[0.3em] px-6 py-3 hover:bg-gold hover:text-white transition-colors duration-300"
        >
          <Plus size={14} /> Add Reel
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="checkout-settings">
        <p className="text-sm font-medium text-[#111827] mb-1">Checkout</p>
        <p className="text-xs text-gray-400 mb-4">Cash on Delivery is always on — set a handling fee to cover courier COD charges (0 for free COD).</p>
        <Field label="COD Fee (₹)" type="number" value={settings.cod_fee ?? 0} onChange={(e) => set("cod_fee", e.target.value)} data-testid="cod-fee-input" />
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="banner-save-btn"
        className="bg-gold text-white text-xs uppercase tracking-[0.3em] px-10 py-4 hover:bg-champagne transition-colors duration-300 disabled:opacity-50"
      >
        {saving ? "Saving…" : "Save Homepage"}
      </button>
    </div>
  );
};
